import { Timestamp } from "./timestamp";

/**
 * User profile document stored at `BusinessMember/{uid}`.
 * Contains identity fields only — no business-scoped access or roles.
 */
export interface BusinessMember {
  uid: string;
  name: string;
  email: string;
  isSuperAdmin?: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  /** @deprecated Use BusinessMembership.role instead. */
  role?: "admin" | "member";
  /** @deprecated No longer written; kept for legacy doc compat. */
  invitedAt?: Timestamp;
  language?: "sv" | "en";
}

export type MembershipRole = "owner" | "admin" | "member";
export type MembershipStatus = "active" | "invited" | "removed";
/**
 * "full" = all product modules (Recruit, Leads, Teams, Intelligence).
 * "team" = team-scoped modules only (Teams, Intelligence, Pulse, Tests).
 * "none" = no people data at all (no Coach, Pulse, people, tests, insights).
 *   Used for report uploaders (GRA-96) who only handle store reports.
 * Absent/undefined is treated as "full" for backward compatibility.
 */
export type MembershipAccessLevel = "full" | "team" | "none";

export const MEMBERSHIP_ACCESS_LEVELS = ["full", "team", "none"] as const;

/** Who may see store report numbers without being a people-data viewer. */
export type ReportViewPermission = "none" | "store";

/**
 * Capabilities granted on top of role and access level. They combine with
 * any role, so a department manager can also be the report uploader.
 */
export interface MembershipPermissions {
  /** May import store reports (Artikelanalys etc.) for the whole business. */
  reportUpload?: boolean;
  /** "store" = may read the store's report numbers (no people data). */
  reportView?: ReportViewPermission;
}

/**
 * Membership document stored at `Businesses/{businessId}/members/{uid}`.
 * Single source of truth for "does this user have access to this business?"
 */
export interface BusinessMembership {
  uid: string;
  name: string;
  email: string;
  role: MembershipRole;
  status: MembershipStatus;
  joinedAt: Timestamp;
  invitedBy?: string;
  /** Absent = "full". Owners and admins always have full access regardless of this field. */
  accessLevel?: MembershipAccessLevel;
  /** Report capabilities (GRA-96). Owners and admins implicitly have all of them. */
  permissions?: MembershipPermissions;
}

type MembershipAccessInput = {
  role?: string | null;
  accessLevel?: string | null;
  permissions?: MembershipPermissions | null;
};

function isOwnerOrAdmin(role: string | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

/**
 * Effective access level. Owners and admins are always "full"; unknown or
 * absent values read as "full" for backward compatibility.
 */
export function effectiveAccessLevel(
  membership: MembershipAccessInput,
): MembershipAccessLevel {
  if (isOwnerOrAdmin(membership.role)) return "full";
  if (membership.accessLevel === "team") return "team";
  if (membership.accessLevel === "none") return "none";
  return "full";
}

/** False for report-only accounts: they must never see Pulse, people or coach data. */
export function hasPeopleDataAccess(membership: MembershipAccessInput): boolean {
  return effectiveAccessLevel(membership) !== "none";
}

export function canUploadReports(membership: MembershipAccessInput): boolean {
  return (
    isOwnerOrAdmin(membership.role) ||
    membership.permissions?.reportUpload === true
  );
}

/**
 * May read the store's report numbers. People-data viewers see them through
 * the KPI views; report-only accounts need `reportView: "store"`.
 */
export function canViewStoreReports(membership: MembershipAccessInput): boolean {
  return (
    hasPeopleDataAccess(membership) ||
    membership.permissions?.reportView === "store"
  );
}

/**
 * @deprecated Use {@link BusinessMembership} instead.
 */
export type BusinessMemberReference = BusinessMembership;
